import type { TableMatrixSaveEvent } from './unified-table-matrix-chart.types';

/**
 * Folds a drill-down save event's cell changes back into the widget's STORED
 * `drilldownData` tree (staticData.data + grand total), mutating it in place.
 *
 * Why this exists: lib-drill-down deliberately edits a CLONE of the tree (its
 * cloneRows), so the host's stored snapshot never sees the edits on its own.
 * That means builder edits would silently miss the publish payload, and any
 * published re-render from the stored widget data would revert saved values.
 * Both hosts call this on the `save` event so the stored snapshot — the one
 * that gets published / re-rendered from — carries the saved values too.
 *
 * Handles both change kinds the drill-down emits (see DrillDownCellChange):
 * - MEASURE edits: `field` is the measure's flat prop; the node is located by
 *   its ancestor path, its value + `measures` mirror update, every ancestor
 *   re-SUMs, and the grand total (separate `total` key OR embedded
 *   isGrandTotal node) re-sums when that measure participates in it.
 * - LABEL renames: `field` is the level's dataset field (matched via the
 *   widget's `levelFields`); the path ends in the NEW name, so the last
 *   segment falls back to `oldValue` for the lookup. Renames apply FIRST,
 *   outermost level first, so measure paths (which carry new names) resolve.
 *
 * Rows without a `values.path` array (i.e. Normal/Editable table saves) are
 * ignored — the function returns false and the caller keeps its generic path.
 */
export function applyDrillDownSaveToWidgetData(
  drilldownData: Record<string, unknown> | null | undefined,
  event: TableMatrixSaveEvent | null | undefined,
): boolean {
  if (!drilldownData || !event?.rows?.length) return false;
  // The builder stores the tree under staticData; a published recompute may
  // have swapped in the raw compute entry (data/total at the top level).
  const staticData = (drilldownData['staticData'] as Record<string, unknown>) || drilldownData;
  const treeRaw = staticData['data'] ?? staticData['Data'];
  if (!Array.isArray(treeRaw)) return false;
  const tree = treeRaw as Array<Record<string, unknown>>;
  const nameProp = String(drilldownData['nameProp'] || 'name');
  const levelFields = (drilldownData['levelFields'] as Record<string, unknown>) || {};
  const dimFields = new Set(Object.values(levelFields).map(String));

  type Parsed = { path: string[]; field: string; value: unknown; oldValue: unknown; level: number; kind?: string };
  const parsed: Parsed[] = event.rows
    .filter((r) => Array.isArray(r?.values?.['path']))
    .map((r) => ({
      path: (r.values['path'] as unknown[]).map(String),
      field: String(r.values['field'] ?? ''),
      value: r.values['value'],
      oldValue: r.values['oldValue'],
      level: Number(r.values['level'] ?? 0),
      kind: r.values['kind'] === 'text' ? 'text' : undefined,
    }))
    .filter((c) => c.path.length && c.field);
  if (!parsed.length) return false;

  const findIn = (nodes: Array<Record<string, unknown>>, name: string, alt?: unknown): Record<string, unknown> | undefined =>
    nodes.find((n) => !n?.['isGrandTotal'] && String(n?.[nameProp] ?? '') === name)
    ?? (alt !== undefined && alt !== null
      ? nodes.find((n) => !n?.['isGrandTotal'] && String(n?.[nameProp] ?? '') === String(alt))
      : undefined);

  /** Walk the stored tree along a display-name path → the node + its ancestors. */
  const locate = (path: string[], lastAlt?: unknown): { node: Record<string, unknown>; ancestors: Array<Record<string, unknown>> } | null => {
    let nodes = tree;
    const ancestors: Array<Record<string, unknown>> = [];
    let node: Record<string, unknown> | undefined;
    for (let i = 0; i < path.length; i++) {
      node = findIn(nodes, path[i], i === path.length - 1 ? lastAlt : undefined);
      if (!node) return null;
      if (i < path.length - 1) ancestors.push(node);
      nodes = Array.isArray(node['children']) ? (node['children'] as Array<Record<string, unknown>>) : [];
    }
    return node ? { node, ancestors } : null;
  };

  const isLabelChange = (c: Parsed): boolean => dimFields.has(c.field);
  let applied = false;

  // ── Label renames first (outermost level first) ──
  parsed.filter(isLabelChange).sort((a, b) => a.level - b.level).forEach((c) => {
    const hit = locate(c.path, c.oldValue);
    if (!hit) return;
    hit.node[nameProp] = String(c.value ?? '');
    applied = true;
  });

  // ── Measure edits + ancestor/grand-total re-sum ──
  parsed.filter((c) => !isLabelChange(c)).forEach((c) => {
    const hit = locate(c.path);
    if (!hit) return;
    // Free-text attribute column: store the string, nothing to re-sum.
    if (c.kind === 'text') {
      const text = c.value === null || c.value === undefined || String(c.value).trim() === '' ? null : String(c.value);
      hit.node[c.field] = text;
      const tm = hit.node['measures'];
      if (tm && typeof tm === 'object') (tm as Record<string, unknown>)[c.field] = text;
      applied = true;
      return;
    }
    const num = c.value === null || c.value === undefined || c.value === '' ? null : Number(c.value);
    const value = num !== null && Number.isFinite(num) ? num : null;
    hit.node[c.field] = value;
    const m = hit.node['measures'];
    if (m && typeof m === 'object') (m as Record<string, unknown>)[c.field] = value;

    for (let i = hit.ancestors.length - 1; i >= 0; i--) {
      const anc = hit.ancestors[i];
      const children = Array.isArray(anc['children']) ? (anc['children'] as Array<Record<string, unknown>>) : [];
      const sum = children.reduce((acc, ch) => {
        const n = Number(ch?.[c.field]);
        return Number.isFinite(n) ? acc + n : acc;
      }, 0);
      anc[c.field] = sum;
      const am = anc['measures'];
      if (am && typeof am === 'object') (am as Record<string, unknown>)[c.field] = sum;
    }

    const topSum = tree
      .filter((n) => !n?.['isGrandTotal'])
      .reduce((acc, n) => {
        const v = Number(n?.[c.field]);
        return Number.isFinite(v) ? acc + v : acc;
      }, 0);
    // Grand total lives either under a separate total key…
    const total = (staticData['total'] ?? staticData['Total']) as Record<string, unknown> | null | undefined;
    if (total && total[c.field] !== undefined && total[c.field] !== null) total[c.field] = topSum;
    // …or embedded as an isGrandTotal node (kaveri-lite engine shape).
    const embedded = tree.find((n) => n?.['isGrandTotal']);
    const em = embedded?.['measures'];
    if (em && typeof em === 'object' && (em as Record<string, unknown>)[c.field] != null) {
      (em as Record<string, unknown>)[c.field] = topSum;
    }
    applied = true;
  });

  return applied;
}
